import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSliders } from '@fortawesome/free-solid-svg-icons';

import { Tab, TabsContainer } from '../../../shared/uiComponents/Tabs';
import { CustomAlert, CustomButton, Text } from '../../../shared/uiComponents';
import { InfoFilled } from '../../../shared/Assets/svgIcons';
import { useRefreshProviderCancellations, useScreenWidth } from '../../../shared/Helpers/hooks';
import { Bars } from '../../../shared/Assets/menuIcons';
import { PEARL } from '../../../shared/Helpers/colors';
import AddSession from '../Actions/addSession';
import RequestCancellation from '../Actions/requestTimeOff';
import { SESSION_STATUSES } from '../Helpers/constants';
import Navigation from './navigation';
import MobileMonthNavigation from './mobileMonthNavigation';
import PendingRequestsChip from './PendingRequestsChip';
import MyPendingRequestsChip from './MyPendingRequestsChip';
import MyApprovedRequestsChip from './MyApprovedRequestsChip';
import MyDeniedRequestsChip from './MyDeniedRequestsChip';

import { DispatchProperties, useSelector } from '../../../redux/store';
import { setApplyFilter, setCalendarView, setOpenFilters } from '../../../redux/State/clientSlice/calendarHelperSlice';

const HEADERS = ['Month', 'Week', 'Day'];

const Tabs = () => {
	const dispatch = useDispatch<DispatchProperties>();

	const view = useSelector((state) => state.calendarHelper.calendarView);

	const viewHandler = (value: number) => {
		dispatch(setCalendarView(value));
	};

	return (
		<TabsContainer className="tabs">
			{Boolean(HEADERS?.length) &&
				HEADERS.map((header, index) => (
					<Tab
						onClick={() => viewHandler(index)}
						key={index}
						id={`${index}`}
						selected={index === view}
						style={{ cursor: 'pointer' }}
					>
						<Text title={header} type="psm" fontWeight={index === view ? 'semiBold' : 'medium'} />
					</Tab>
				))}
		</TabsContainer>
	);
};

const SessionStatusInfoContent = () => (
	<div
		style={{
			display: 'flex',
			flexDirection: 'column',
			gap: 16,
		}}
	>
		{SESSION_STATUSES.map((item) => {
			const { Icon, label } = item;

			return (
				<div style={{ display: 'flex', gap: 8, alignItems: 'center' }} key={label}>
					<Icon />
					<Text title={`- ${label}`} />
				</div>
			);
		})}
	</div>
);

const MobileTabs = () => {
	const [open, setOpen] = useState<boolean>(false);

	return (
		<>
			<CustomButton iconOnly={<Bars color={PEARL.BASE[600]} />} buttonType="quaternary" onClick={() => setOpen(true)} aria-label="Switch calendar view" />
			<CustomAlert open={open} title="Calendar" onClose={() => setOpen(false)} Content={Tabs} />
		</>
	);
};

const SessionStatusInfo = () => {
	const [open, setOpen] = useState<boolean>(false);
	const closeHandler = () => setOpen(false);
	const openHandler = () => setOpen(true);

	return (
		<>
			<CustomButton
				iconOnly={<InfoFilled />}
				onClick={openHandler}
				buttonType="secondaryGray"
				className="sessionInfoAction"
				aria-label="Session status details"
			/>
			<CustomAlert
				title="Details for session statuses"
				open={open}
				onClose={closeHandler}
				titleOnTop={false}
				Content={SessionStatusInfoContent}
			/>
		</>
	);
};

const CalendarHeader = ({ showSessionControls }: { showSessionControls?: boolean }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;
	const view = useSelector((state) => state.calendarHelper.calendarView);
	const refreshCancellations = useRefreshProviderCancellations();

	useEffect(() => {
		refreshCancellations();
	}, [refreshCancellations]);

	const openFilterHandler = () => {
		dispatch(setOpenFilters(true));
		dispatch(setApplyFilter(true));
	};

	// For mobile month view, use the new mobile navigation
	if (isMobile && view === 0) {
		return <MobileMonthNavigation onOpenFilters={openFilterHandler} />;
	}

	return (
		<div className="calendarHeader">
			{isMobile && <MobileTabs />}
			<Navigation />
			<div className="actions">
				<PendingRequestsChip />
				<MyPendingRequestsChip />
				<MyApprovedRequestsChip />
				<MyDeniedRequestsChip />
				<CustomButton
					iconOnly={<FontAwesomeIcon icon={faSliders} />}
					buttonType="secondaryGray"
					onClick={openFilterHandler}
					aria-label="Filter calendar"
				/>
				{(isMobile || showSessionControls) && <AddSession />}
				{(isMobile || showSessionControls) && <RequestCancellation />}
				{!isMobile && <Tabs />}
				<SessionStatusInfo />
			</div>
		</div>
	);
};

export default CalendarHeader;
